import { FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Chat } from './Chat';
import AssistantLibrary from './components/AssistantLibrary';
import { DeleteChatModal } from './components/DeleteChatModal';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { SIDEBAR_SPECIALISTS } from './Utils/specialists';
import useAiChatModal from './useAiChatModal';
import { ChatArea, Content, Panel, Scrim } from './styles';

export interface AiChatProps {
  constructionSiteId: number;
  showAiModal: boolean;
  toggleAiModal: () => void;
}

/** Painel do Coordia (Figma "Chat IA Web v26"). Fica montado para preservar a conversa. */
export const AiChatModal: FC<AiChatProps> = ({
  constructionSiteId,
  showAiModal,
  toggleAiModal
}) => {
  const { t } = useTranslation('activities');
  const {
    isSidebarOpen,
    toggleSidebar,
    isExpanded,
    toggleExpanded,
    collapse,
    chatSessionKey,
    selectedThreadId,
    setSelectedThreadId,
    selectedThreadTitle,
    currentView,
    selectedAssistant,
    groups,
    hasThreads,
    isLoadingThreads,
    isSearchOpen,
    search,
    setSearch,
    toggleSearch,
    renamingThreadId,
    setRenamingThreadId,
    submitRename,
    threadToDelete,
    setThreadToDelete,
    confirmDelete,
    isDeleting,
    startNewChat,
    selectThread,
    selectSpecialist,
    selectLibraryAssistant,
    toggleLibrary
  } = useAiChatModal(constructionSiteId);

  const headerTitle =
    currentView === 'assistant-library'
      ? t('aiChat.library.title')
      : selectedThreadTitle ?? t('aiChat.newChat');

  return createPortal(
    <>
      {showAiModal && isExpanded && <Scrim onClick={collapse} />}
      <Panel
        role="dialog"
        aria-label={t('aiChat.title')}
        $open={showAiModal}
        $sidebarOpen={isSidebarOpen}
        $expanded={isExpanded}
      >
        {isSidebarOpen && (
          <Sidebar
            groups={groups}
            hasThreads={hasThreads}
            isLoading={isLoadingThreads}
            search={search}
            isSearchOpen={isSearchOpen}
            selectedThreadId={selectedThreadId}
            renamingThreadId={renamingThreadId}
            specialists={SIDEBAR_SPECIALISTS}
            isLibraryOpen={currentView === 'assistant-library'}
            onSearchChange={setSearch}
            onToggleSearch={toggleSearch}
            onNewChat={startNewChat}
            onSelectThread={selectThread}
            onOpenLibrary={toggleLibrary}
            onSelectSpecialist={selectSpecialist}
            onStartRename={setRenamingThreadId}
            onSubmitRename={submitRename}
            onCancelRename={() => setRenamingThreadId(undefined)}
            onRequestDelete={setThreadToDelete}
          />
        )}

        <Content>
          <Header
            title={headerTitle}
            isSidebarOpen={isSidebarOpen}
            isExpanded={isExpanded}
            showNewChat={!isSidebarOpen && Boolean(selectedThreadId)}
            onToggleSidebar={toggleSidebar}
            onNewChat={startNewChat}
            onToggleExpanded={toggleExpanded}
            onClose={toggleAiModal}
          />
          <ChatArea>
            {currentView === 'chat' ? (
              <Chat
                key={chatSessionKey}
                constructionSiteId={constructionSiteId}
                selectedThreadId={selectedThreadId}
                setSelectedThreadId={setSelectedThreadId}
                assistant={selectedAssistant}
                onViewAll={toggleLibrary}
              />
            ) : (
              <AssistantLibrary onSelectAssistant={selectLibraryAssistant} />
            )}
          </ChatArea>
        </Content>
      </Panel>

      {threadToDelete && (
        <DeleteChatModal
          chatName={threadToDelete.title}
          isDeleting={isDeleting}
          onCancel={() => setThreadToDelete(undefined)}
          onConfirm={confirmDelete}
        />
      )}
    </>,
    document.body
  );
};
